/**
 * Public, read-only broker view of a claims analysis.
 *
 * Reached via a signed share link (/claims/shared/:token) — no login.
 * Renders ONLY the aggregate analysis the backend exposes (stats + AI
 * sections); never line items or member data. Self-contained layout so
 * it works outside the authed app shell.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { formatCurrency } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { viewSharedClaims } from "@/api/claims";
import type { ClaimsStats, ClaimsAi } from "@/api/claims";

function Bar({ pct, label, value }: { pct: number; label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-text-primary truncate">{label}</span>
        <span className="text-text-muted ml-2 shrink-0">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-background overflow-hidden">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  );
}

export function ClaimsSharedPage() {
  const { token = "" } = useParams();
  const [data, setData] = useState<{
    carrier_name: string | null;
    report_period_start: string | null;
    report_period_end: string | null;
    stats: ClaimsStats | null;
    ai: ClaimsAi | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    viewSharedClaims(token)
      .then(setData)
      .catch((e) => {
        const status = (e as { response?: { status?: number } })?.response
          ?.status;
        setError(
          status === 410
            ? "This link has expired."
            : status === 429
              ? "Too many requests — please try again later."
              : "This link is no longer available.",
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <span className="font-heading font-semibold text-text-primary">
            Lenavio
          </span>
          <Badge variant="default">Read-only · shared report</Badge>
        </div>

        {loading && <Card><p className="text-sm text-text-muted">Loading…</p></Card>}
        {error && (
          <Card>
            <p className="text-sm text-text-primary">{error}</p>
          </Card>
        )}

        {data?.stats && (
          <>
            <div>
              <h1 className="text-xl font-semibold font-heading text-text-primary">
                Claims Analysis — {data.carrier_name || "Carrier"}
              </h1>
              {data.report_period_start && (
                <p className="text-sm text-text-muted">
                  {data.report_period_start} → {data.report_period_end}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card variant="flat" className="p-4">
                <p className="text-[11px] uppercase text-text-muted">Total spend</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {formatCurrency(data.stats.total_spend)}
                </p>
              </Card>
              <Card variant="flat" className="p-4">
                <p className="text-[11px] uppercase text-text-muted">PMPM</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {formatCurrency(data.stats.pmpm)}
                </p>
              </Card>
              <Card variant="flat" className="p-4">
                <p className="text-[11px] uppercase text-text-muted">Claims</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {data.stats.total_claims.toLocaleString()}
                </p>
              </Card>
              <Card variant="flat" className="p-4">
                <p className="text-[11px] uppercase text-text-muted">Members</p>
                <p className="text-2xl font-semibold text-text-primary">
                  {data.stats.total_members}
                </p>
              </Card>
            </div>

            <Card>
              <h3 className="text-sm font-semibold text-text-primary mb-3">
                Spend by diagnosis
              </h3>
              <div className="space-y-3">
                {data.stats.by_diagnosis.slice(0, 8).map((d) => (
                  <Bar
                    key={d.category}
                    label={d.category}
                    value={`${formatCurrency(d.spend)} (${d.pct}%)`}
                    pct={d.pct}
                  />
                ))}
              </div>
            </Card>

            {data.ai && !data.ai.error && data.ai.top_findings && (
              <Card>
                <h3 className="text-sm font-semibold text-text-primary mb-3">
                  Top findings
                </h3>
                <ol className="space-y-2 list-decimal ml-4">
                  {data.ai.top_findings.map((f, i) => (
                    <li key={i} className="text-sm">
                      <span className="text-text-primary font-medium">
                        {f.title}
                      </span>
                      <p className="text-text-muted mt-0.5">{f.detail}</p>
                    </li>
                  ))}
                </ol>
              </Card>
            )}
            {data.ai?.program_recommendations && (
              <Card>
                <h3 className="text-sm font-semibold text-text-primary mb-3">
                  Recommendations
                </h3>
                <ul className="space-y-2">
                  {data.ai.program_recommendations.map((p, i) => (
                    <li key={i} className="text-sm">
                      <span className="text-text-primary font-medium">
                        {p.program}
                      </span>
                      <p className="text-text-muted mt-0.5">{p.rationale}</p>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <p className="text-xs text-text-muted text-center">
              Aggregate, de-identified analysis. No individual member
              information is included. Powered by Lenavio.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
